import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

const PersonalNotes = ({ 
  contentId, 
  contentType, 
  title,
  isOpen = false,
  onClose = () => {},
  className = ""
}) => {
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [editingNote, setEditingNote] = useState(null);
  const [editText, setEditText] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const noteKey = `${contentType}_${contentId}`;

  // Load notes from localStorage
  useEffect(() => {
    const savedNotes = JSON.parse(localStorage.getItem('gitsawe_personal_notes') || '{}');
    const contentNotes = savedNotes[noteKey] || [];
    setNotes(contentNotes);
  }, [noteKey]);

  // Save notes to localStorage
  const saveNotes = (updatedNotes) => {
    const allNotes = JSON.parse(localStorage.getItem('gitsawe_personal_notes') || '{}');
    allNotes[noteKey] = updatedNotes;
    localStorage.setItem('gitsawe_personal_notes', JSON.stringify(allNotes));
    setNotes(updatedNotes);
  };

  // Add new note
  const handleAddNote = () => {
    if (!newNote.trim()) {
      toast.error('እባክዎ ማስታወሻ ይጻፉ');
      return;
    }

    const note = {
      id: Date.now(),
      text: newNote.trim(),
      timestamp: new Date().toISOString(),
      contentTitle: title
    };

    const updatedNotes = [...notes, note];
    saveNotes(updatedNotes);
    setNewNote('');
    toast.success('📝 ማስታወሻ ተጨምሯል!');
  };

  // Edit note
  const handleEditNote = (noteId) => {
    const note = notes.find(n => n.id === noteId);
    if (note) {
      setEditingNote(noteId);
      setEditText(note.text);
    }
  };

  // Save edited note
  const handleSaveEdit = () => {
    if (!editText.trim()) {
      toast.error('እባክዎ ማስታወሻ ይጻፉ');
      return;
    }

    const updatedNotes = notes.map(note => 
      note.id === editingNote 
        ? { ...note, text: editText.trim(), lastEdited: new Date().toISOString() }
        : note
    );

    saveNotes(updatedNotes);
    setEditingNote(null);
    setEditText('');
    toast.success('📝 ማስታወሻ ተስተካክሏል!');
  };

  // Delete note
  const handleDeleteNote = (noteId) => {
    if (window.confirm('ይህን ማስታወሻ መሰረዝ ይፈልጋሉ?')) {
      const updatedNotes = notes.filter(note => note.id !== noteId);
      saveNotes(updatedNotes);
      toast.success('🗑️ ማስታወሻ ተሰርዟል');
    }
  };

  // Filter notes based on search
  const filteredNotes = notes.filter(note =>
    note.text.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('am-ET', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-purple-500 text-white p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold flex items-center gap-2">
                📝 የግል ማስታወሻዎች
              </h2>
              <p className="text-blue-100 mt-1">{title}</p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 text-2xl font-bold"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[calc(90vh-120px)] overflow-y-auto">
          {/* Add New Note */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              አዲስ ማስታወሻ ጨምር
            </label>
            <div className="flex gap-2">
              <textarea
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="የእርስዎን ማስታወሻ እዚህ ይጻፉ..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                rows="3"
              />
              <button
                onClick={handleAddNote}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors duration-200 h-fit"
              >
                ጨምር
              </button>
            </div>
          </div>

          {/* Search Notes */}
          {notes.length > 0 && (
            <div className="mb-4">
              <input
                type="text"
                placeholder="በማስታወሻዎች ውስጥ ፈልግ..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          )}

          {/* Notes List */}
          <div className="space-y-4">
            {filteredNotes.length > 0 ? (
              filteredNotes.map((note) => (
                <div key={note.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  {editingNote === note.id ? (
                    <div className="space-y-3">
                      <textarea
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                        rows="3"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={handleSaveEdit}
                          className="px-3 py-1 bg-green-500 text-white rounded text-sm hover:bg-green-600 transition-colors"
                        >
                          አስቀምጥ
                        </button>
                        <button
                          onClick={() => {
                            setEditingNote(null);
                            setEditText('');
                          }}
                          className="px-3 py-1 bg-gray-500 text-white rounded text-sm hover:bg-gray-600 transition-colors"
                        >
                          ሰርዝ
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <p className="text-gray-800 mb-2 whitespace-pre-wrap">{note.text}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">
                          {formatDate(note.timestamp)}
                          {note.lastEdited && (
                            <span className="ml-2">(ተስተካክሏል: {formatDate(note.lastEdited)})</span>
                          )}
                        </span>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEditNote(note.id)}
                            className="text-blue-500 hover:text-blue-700 text-sm"
                          >
                            ✏️ አርም
                          </button>
                          <button
                            onClick={() => handleDeleteNote(note.id)}
                            className="text-red-500 hover:text-red-700 text-sm"
                          >
                            🗑️ ሰርዝ
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))
            ) : notes.length > 0 ? (
              <div className="text-center py-8">
                <div className="text-4xl mb-2">🔍</div>
                <p className="text-gray-500">ምንም ማስታወሻ አልተገኘም</p>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-4xl mb-2">📝</div>
                <p className="text-gray-500">ገና ምንም ማስታወሻ አልተጨመረም</p>
                <p className="text-gray-400 text-sm">ከላይ ያለውን ሳጥን በመጠቀም የመጀመሪያ ማስታወሻዎን ይጨምሩ</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonalNotes;